import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Layout,
  Row,
  Col,
  Card,
  Typography,
  Table,
  Spin,
  Empty,
  Alert,
  Avatar,
  Button,
  Space,
  Statistic,
  Skeleton,
  Grid,
} from "antd";
import { TeamOutlined, BookOutlined, ReloadOutlined, FieldTimeOutlined, CommentOutlined } from "@ant-design/icons";
import useTokenExpiryCheck from "../../utils/UseTokenExpiryCheck";
import { clearDashboardError, fetchStudentSubjects } from "../../store/dashboardSlice";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { useBreakpoint } = Grid;

// ----- Styles (kept as JS objects for clarity & easy overrides) -----
const styles = {
  page: (open, mobile) => ({
    padding: mobile ? 12 : 24,
    paddingLeft: mobile ? 12 : open ? 200 : 60,
    paddingRight: mobile ? 12 : 16,
    background: "#f5f7fa",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "flex-start",
  }),
  wrapper: {
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  header: { textAlign: "left", marginBottom: 8 },
  card: {
    borderRadius: 12,
    boxShadow: "0 6px 18px rgba(12, 40, 70, 0.06)",
    background: "#fff",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  meta: { display: "flex", gap: 24, alignItems: "center" },
};

// ----- Small helpers -----
const safeParse = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}") || {};
  } catch (e) {
    console.error("Failed to parse localStorage key", key, e);
    return {};
  }
};

const getStudentIdFromUserInfo = (userInfo) => {
  if (!userInfo) return null;
  if (userInfo.fullStudentInfo && userInfo.fullStudentInfo.length > 0) return userInfo.fullStudentInfo[0].studentId;
  if (userInfo.studentIds && userInfo.studentIds.length > 0) return userInfo.studentIds[0];
  return null;
};

const hasStudentRole = (userInfo) =>
  Array.isArray(userInfo?.roles) &&
  (userInfo.roles.includes("Student") || userInfo.roles.includes("Parent"));

// ----- Header -----
const DashboardHeader = ({ username, lastLogin, mobile }) => (
  <div style={styles.header}>
    <Row justify="space-between" align="middle">
      <Col flex="auto">
        <Title level={mobile ? 4 : 3} style={{ margin: 0 }}>
          Hello, {username || "Student"}
        </Title>
        <Paragraph style={{ margin: 0, color: "rgba(0,0,0,0.6)" }}>
          Academic overview & subjects summary
        </Paragraph>
      </Col>
      {!mobile && (
        <Col>
          <Space>
          </Space>
        </Col>
      )}
    </Row>
  </div>
);

// ----- Student Info Card (memoized) -----
const StudentInfoCard = React.memo(({ studentInfo, mobile }) => {
  // Accept studentInfo from props (dashboard slice) or fallback to localStorage
  const fallback = safeParse("userInfo");
  const studentData = studentInfo || (fallback.fullStudentInfo && fallback.fullStudentInfo.length > 0 ? fallback.fullStudentInfo[0] : null);

  if (!studentData) {
    return (
      <Card hoverable style={styles.card} bodyStyle={{ padding: mobile ? 12 : 20 }}>
        <Empty description="No student selected" />
      </Card>
    );
  }

  const title = (
    <div style={styles.sectionTitle}>
      <Avatar size={mobile ? 40 : 56} icon={<TeamOutlined />} style={{ background: "#e6f7ff", color: "#1890ff" }} />
      <div>
        <div style={{ color: "rgba(0,0,0,0.45)", fontSize: mobile ? 12 : 14 }}>{studentData.batchName ? `${studentData.batchName} • ${studentData.sectionName || ""}` : studentData.sectionName || ""}</div>
      </div>
    </div>
  );

  return (
    <Card hoverable style={styles.card} bodyStyle={{ padding: mobile ? 12 : 20 }} title={title}>
      <Row gutter={[12, 12]}>
        <Col span={mobile ? 24 : 8}>
          <Text type="secondary">Student Name</Text>
          <div><Text strong>{studentData.studentName || "—"}</Text></div>
        </Col>

        <Col span={mobile ? 12 : 8}>
          <Text type="secondary">Current Batch</Text>
          <div><Text>{studentData.batchName || "—"}</Text></div>
        </Col>

        <Col span={mobile ? 12 : 8}>
          <Text type="secondary">Section</Text>
          <div><Text>{studentData.sectionName || "—"}</Text></div>
        </Col>
      </Row>
    </Card>
  );
});

// ----- Subjects Table Card -----
const SubjectsCard = React.memo(({ subjects, loading, mobile }) => {
  const totalContactHours = useMemo(() => (subjects || []).reduce((s, x) => s + (Number(x.contactHour) || 0), 0), [subjects]);
  const totalExpectedComments = useMemo(() => (subjects || []).reduce((s, x) => s + (Number(x.expectedCommentCount) || 0), 0), [subjects]);

  const columns = useMemo(() => [
    {
      title: "Subject Name",
      dataIndex: "name",
      key: "name",
      render: (value) => <Text strong>{value || "—"}</Text>,
    },
    {
      title: "Weight",
      dataIndex: "weight",
      key: "weight",
      align: "center",
      render: (w) => (w != null ? w : "—"),
    },
    {
      title: "Contact Hours",
      dataIndex: "contactHour",
      key: "contactHour",
      align: "center",
      render: (h) => (h != null ? h : 0),
    },
    {
      title: "Expected Comments",
      dataIndex: "expectedCommentCount",
      key: "expectedCommentCount",
      align: "center",
      render: (c) => (c != null ? c : 0),
    },
  ], []);

  return (
    <Card hoverable style={styles.card} bodyStyle={{ padding: mobile ? 12 : 20 }}
      title={(
        <div style={styles.sectionTitle}>
          <Avatar icon={<BookOutlined />} style={{ background: "#f6ffed", color: "#52c41a" }} />
          <div>
            <Text strong>Subjects</Text>
            <div style={{ color: "rgba(0,0,0,0.45)" }}>Detailed subject list and workload overview</div>
          </div>
        </div>
      )}
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Statistic title="Total Subjects" value={(subjects || []).length} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Statistic title="Contact Hours" value={totalContactHours} prefix={<FieldTimeOutlined />} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Statistic title="Expected Comments" value={totalExpectedComments} prefix={<CommentOutlined />} />
        </Col>
      </Row>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (subjects && subjects.length > 0 ? (
        <Table
          rowKey={(record) => record.subId || record.id || record.name}
          dataSource={subjects}
          columns={columns}
          pagination={false}
          bordered
          size={mobile ? "small" : "middle"}
          scroll={{ x: "max-content" }}
        />
      ) : (
        <Empty description="No subjects available" />
      ))}
    </Card>
  );
});

// ----- Dashboard (Main) -----
const Dashboard = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((s) => s.auth || {});
  const { studentInfo, subjects, loading, status, error } = useSelector((s) => s.dashboard || {});
  const isOpen = useSelector((s) => s.ui?.sidebarOpen);
  const reduxMobile = useSelector((s) => s.ui?.isMobileView);

  useTokenExpiryCheck();

  const screens = useBreakpoint();
  const localIsMobile = !screens.md; // true for widths smaller than md (~768px)
  const mobile = Boolean(reduxMobile || localIsMobile);

  const userInfo = safeParse("userInfo");
  const studentId = getStudentIdFromUserInfo(userInfo);
  const shouldLoadStudentSubjects = hasStudentRole(userInfo) && Boolean(studentId);

  // fetch on mount or when studentId becomes available
  useEffect(() => {
    if (!shouldLoadStudentSubjects) {
      dispatch(clearDashboardError());
      return;
    }

    if (isAuthenticated && (status === "idle" || status === "failed")) {
      dispatch(fetchStudentSubjects(studentId));
    }
  }, [dispatch, isAuthenticated, shouldLoadStudentSubjects, studentId, status]);

  const handleRefresh = useCallback(() => {
    if (shouldLoadStudentSubjects) dispatch(fetchStudentSubjects(studentId));
  }, [dispatch, shouldLoadStudentSubjects, studentId]);

  // Quick retry UI when errors happen
  const ErrorBlock = () => (
    <Alert
      message="Unable to load dashboard"
      description={
        <span>
          {error || "An unexpected error occurred while fetching data."} <Button size="small" onClick={handleRefresh} icon={<ReloadOutlined />}>Retry</Button>
        </span>
      }
      type="error"
      showIcon
      style={{ marginBottom: 16 }}
    />
  );

  if (!isAuthenticated) return null; // guard

  return (
    <Layout>
      <Content style={styles.page(isOpen, mobile)}>
        <div style={styles.wrapper}>
          <DashboardHeader username={userInfo.username} lastLogin={userInfo.lastLogin} mobile={mobile} />

          {shouldLoadStudentSubjects && error && <ErrorBlock />}

          {shouldLoadStudentSubjects && (loading || status === "loading") ? (
            <div style={{ display: "flex", justifyContent: "center", padding: mobile ? 24 : 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {shouldLoadStudentSubjects ? (
                <>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} sm={24} md={10}>
                      <StudentInfoCard studentInfo={studentInfo} mobile={mobile} />
                    </Col>

                    <Col xs={24} sm={24} md={14}>
                      <SubjectsCard subjects={subjects} loading={loading} mobile={mobile} />
                    </Col>
                  </Row>

                  <Row justify="end" style={{ marginTop: 8 }}>
                    <Col>
                      <Space>
                        <Button onClick={handleRefresh} icon={<ReloadOutlined />}>Refresh</Button>
                      </Space>
                    </Col>
                  </Row>
                </>
              ) : (
                <Card hoverable style={styles.card} bodyStyle={{ padding: mobile ? 12 : 20 }}>
                  <Empty description="No student dashboard data for this account" />
                </Card>
              )}
            </>
          )}
        </div>
      </Content>
    </Layout>
  );
};

export default Dashboard;
